/** Membership lists: a record's ordered list of the row ids of one of its collections
 *  (`additional_custom_ids.order` on a tour, `day.slots` on an itinerary day). A record
 *  kind declares every such path it has (`membershipPaths`) and hands the set to what
 *  needs it — there is no registry, so a consumer never depends on which modules happened
 *  to load:
 *
 *  - `nextTempId` — a new row's id is below every negative id any of the lists holds, and
 *    below every one the session ever issued (`DraftState.tempIdFloor`);
 *  - a refetch (`membershipFreshFrom`, `drift.ts`) — a list both sides changed is merged
 *    (`mergeIdList`), never drifted.
 *
 *  ONE SET PER KIND. A record kind declares its set once, COMPLETE (every collection's
 *  list and every outline level's), and every spec and operation of the kind passes that
 *  same frozen instance — never a subset. `nextTempId` reads it whole: a new row whose
 *  fields are all still their defaults has no entries of its own and is discoverable
 *  only as a member of its parent's list, so a set that misses that list hands out an id
 *  already in use. In development, an operation that passes another set instance for a
 *  kind that already used one fails loudly (`assertKindMemberships`). */
import { parseRecordKey, type DraftState, type RecordKey } from '@/features/records/draft/changeSet';

/** A record kind's membership-list paths — one frozen set per kind (see above). */
export type MembershipPaths = ReadonlySet<string>;

function refuse(): never {
  throw new TypeError('A membership set is frozen: declare every path of the kind in membershipPaths()');
}

/** A record kind's ONE membership set, from all its lists (its collections', its
 *  outline's). Frozen: adding a path later would leave the specs that already read it
 *  behind. */
export function membershipPaths(...lists: Iterable<string>[]): MembershipPaths {
  const out = new Set<string>();
  for (const list of lists) for (const path of list) out.add(path);
  return Object.freeze(Object.assign(out, { add: refuse, delete: refuse, clear: refuse }));
}

/** Dev-only: the set each owning record kind (the owner record's entity) passed first. */
const kindSets = new Map<string, MembershipPaths>();

/** Dev-only invariant (a no-op in a production build): every operation of a record kind
 *  passes the kind's ONE membership set. Keyed by the owning record's entity (`tour:42` →
 *  `tour`); another set instance for a kind that already used one throws under test and
 *  logs an error in development. */
export function assertKindMemberships(ownerRec: RecordKey, memberships: MembershipPaths): void {
  if (!import.meta.env.DEV) return;
  const kind = parseRecordKey(ownerRec).entity;
  const first = kindSets.get(kind);
  if (first === undefined) {
    kindSets.set(kind, memberships);
    return;
  }
  if (first === memberships) return;
  const message =
    `Record kind "${kind}" passed a membership set other than the one it used before ` +
    `(${[...first].join(', ')} vs ${[...memberships].join(', ')}): every spec and operation of a ` +
    'kind must pass its ONE complete set, or nextTempId can hand out an id already in use.';
  if (import.meta.env.MODE === 'test') throw new Error(message);
  console.error(message);
  // Said once per new set (a hot reload of the declaring module re-creates it).
  kindSets.set(kind, memberships);
}

export function isMembershipPath(path: string, memberships: MembershipPaths): boolean {
  return memberships.has(path);
}

/** The next id for a row the draft creates: below every negative id in use — as a
 *  record of the draft or a member of one of the kind's membership lists (a new row whose
 *  fields are all still their defaults has no entries of its own) — and below every id the
 *  editing session issued before (`tempIdFloor`: one a save in flight sent, or an undo took
 *  out of the entries, is never issued again). The action that hands it out carries it in
 *  `issued`, which lowers the floor. */
export function nextTempId(
  state: Pick<DraftState, 'entries'> & Partial<Pick<DraftState, 'tempIdFloor'>>,
  memberships: MembershipPaths,
): number {
  let min = Math.min(0, state.tempIdFloor ?? 0);
  for (const [rec, recPaths] of Object.entries(state.entries)) {
    const id = Number(rec.slice(rec.lastIndexOf(':') + 1));
    if (Number.isFinite(id) && id < min) min = id;
    for (const [path, entry] of Object.entries(recPaths)) {
      if (!isMembershipPath(path, memberships) || !Array.isArray(entry.to)) continue;
      for (const member of entry.to) if (typeof member === 'number' && member < min) min = member;
    }
  }
  return min - 1;
}

/** An id list both sides changed, merged: the draft's order and edits, minus the rows the
 *  server removed, plus the rows the server added (at the end). Whatever the draft added
 *  stays — new rows (negative ids) and existing ones it picked; only ids the draft had
 *  from the server and the server no longer has are dropped. */
export function mergeIdList(from: unknown, to: unknown, current: unknown): number[] {
  const f = asIds(from);
  const t = asIds(to);
  const c = asIds(current);
  const out = t.filter((id) => c.includes(id) || !f.includes(id));
  for (const id of c) if (!f.includes(id) && !out.includes(id)) out.push(id);
  return out;
}

function asIds(v: unknown): number[] {
  return Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : [];
}
